import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";

import { ThemeProvider, useTheme } from "./context/ThemeContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ThemeSidePanel from "./components/ThemeSidePanel";
import LoadingScreen from "./components/LoadingScreen";
import ScrollProgress from "./components/ScrollProgress";
import MusicAutoPlayer from "./components/MusicAutoPlayer";
import ParticlesBackground from "./components/ParticlesBackground";
import StarsBackground from "./components/StarsBackground";
import GradientBackground from "./components/GradientBackground";

import GeoToast from "./components/GeoToast";
import TimeOverlay from "./components/TimeOverlay";

import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ProjectsPage from "./pages/ProjectsPage";
import RepositoriesPage from "./pages/RepositoriesPage";
import TechnologiesPage from "./pages/TechnologiesPage";
import ContactPage from "./pages/ContactPage";
import AdminPage from "./pages/AdminPage";

const BackgroundEffect = () => {
  const { bgEffect } = useTheme();
  if (bgEffect === 'particles') return <ParticlesBackground key="bg-particles" />;
  if (bgEffect === 'stars') return <StarsBackground key="bg-stars" />;
  if (bgEffect === 'gradient') return <GradientBackground key="bg-gradient" />;
  return null;
};

const PageWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
        <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
        <Route path="/projects" element={<PageWrapper><ProjectsPage /></PageWrapper>} />
        <Route path="/repositories" element={<PageWrapper><RepositoriesPage /></PageWrapper>} />
        <Route path="/technologies" element={<PageWrapper><TechnologiesPage /></PageWrapper>} />
        <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </AnimatePresence>
  );
};

const AppContent = () => {
  const { isLoading } = useTheme();
  const location = useLocation();
  const isAdmin = location.pathname === '/admin';

  // Remove platform badge
  useEffect(() => {
    const hide = () => {
      document.querySelectorAll('a[href*="emergent"], div[class*="emergent"]').forEach(el => {
        const p = el.closest('div[style*="position"]') || el.parentElement;
        if (p && p !== document.body) p.style.display = 'none';
        el.style.display = 'none';
      });
    };
    hide();
    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App bg-[#050505] min-h-screen">
      {/* Loading Screen */}
      <AnimatePresence>
        {isLoading && <LoadingScreen />}
      </AnimatePresence>

      {/* Dynamic Background */}
      {!isAdmin && <BackgroundEffect />}

      {/* Time-of-day Overlay */}
      {!isAdmin && <TimeOverlay />}

      {/* Scroll Progress */}
      {!isAdmin && <ScrollProgress />}

      {/* Music Auto Player */}
      {!isAdmin && <MusicAutoPlayer />}

      {/* Geo Toast */}
      {!isAdmin && <GeoToast />}

      {/* Theme Side Panel */}
      {!isAdmin && <ThemeSidePanel />}

      {/* Header */}
      {!isAdmin && <Header />}

      {/* Main Content */}
      <main>
        <AnimatedRoutes />
      </main>

      {/* Footer */}
      {!isAdmin && <Footer />}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
